import apiClient from '@/lib/api-client';

export interface CategoryData {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    parentId: string | null;
    sortOrder: number;
    isActive: boolean;
    color?: string;
    icon?: string;
    productCount?: number;
    children?: CategoryData[];
}

// UPDATED: Now matches backend response including blocked state
export interface DeleteCategoryResult {
    message?: string;
    productCount: number;
    childCount: number;
    blocked?: boolean; // true if deletion was blocked due to products
}

export interface BulkCategoryResult {
    uploadId: string;
    totalRows: number;
    successCount: number;
    errorCount: number;
    skippedCount: number;
    errors: Array<{ row: number; categoryName: string; field: string; message: string; suggestedFix?: string }>;
    hasErrors: boolean;
}

export async function getCategoriesTree(tenantId: string): Promise<CategoryData[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/categories/tree`);
    return data.data;
}

export async function getCategoriesFlat(tenantId: string): Promise<CategoryData[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/categories`);
    return data.data;
}

export async function getCategory(tenantId: string, categoryId: string): Promise<CategoryData> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/categories/${categoryId}`);
    return data.data;
}

export async function createCategory(tenantId: string, payload: {
    name: string; slug: string; description?: string; parentId?: string;
    sortOrder?: number; color?: string; icon?: string;
}): Promise<CategoryData> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/categories`, payload);
    return data.data;
}

export async function updateCategory(tenantId: string, categoryId: string, payload: {
    name?: string; slug?: string; description?: string | null;
    parentId?: string | null; sortOrder?: number; isActive?: boolean;
    color?: string; icon?: string;
}): Promise<CategoryData> {
    const { data } = await apiClient.patch(`/tenants/${tenantId}/categories/${categoryId}`, payload);
    return data.data;
}

/**
 * Delete a category.
 * 
 * If the category has products, the backend returns { blocked: true, productCount, message }.
 * The frontend should check `blocked` and show a reassign modal instead of treating it as success.
 * 
 * @throws {AxiosError} with response.data.error if deletion is blocked (400 Bad Request)
 */
export async function deleteCategory(tenantId: string, categoryId: string): Promise<DeleteCategoryResult> {
    const { data } = await apiClient.delete(`/tenants/${tenantId}/categories/${categoryId}`);
    return data.data;
}

// NEW: Restore soft-deleted category
export async function restoreCategory(tenantId: string, categoryId: string): Promise<CategoryData> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/categories/${categoryId}/restore`);
    return data.data;
}

// UPDATED: Now returns the moved count
export async function reassignProducts(
    tenantId: string,
    fromCategoryId: string,
    toCategoryId: string
): Promise<{ movedCount: number; message: string }> {
    const { data } = await apiClient.post(
        `/tenants/${tenantId}/categories/${fromCategoryId}/reassign`,
        { targetCategoryId: toCategoryId }
    );
    return data.data;
}

// REMOVED: validateSlug — no backend endpoint exists. Use client-side validation instead.

export async function downloadCategoryTemplate(tenantId: string): Promise<Blob> {
    const response = await apiClient.get(`/tenants/${tenantId}/categories/template/download`, { responseType: 'blob' });
    return response.data;
}

export async function bulkUploadCategories(tenantId: string, file: File): Promise<BulkCategoryResult> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post(`/tenants/${tenantId}/categories/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.data;
}